/**
 * PostHog analytics initialization.
 * Uses VITE_POSTHOG_KEY env var (set via Doppler or .env).
 * PostHog project API keys are public by design (like GA tracking IDs).
 *
 * DEV-12078: Optimized configuration to reduce bundle size.
 * Disabled session recording, heatmaps, and performance capture which add
 * ~60-80 KiB to the bundle. Marketing site only needs basic event tracking.
 *
 * DEV-12125: posthog-js (~60 KiB, the `vendor-analytics` chunk) is now loaded
 * with a DYNAMIC import instead of a static one. Statically importing it here
 * pulled it into the entry graph — every route's critical JS carried it, and it
 * had to download + parse before React could mount. Because the lab mobile LCP
 * element is React's re-rendered hero text, LCP is gated on that mount, so 60 KiB
 * of analytics sat on the LCP critical path even though initPostHog() was already
 * deferred to idle. The import now resolves inside initPostHog (fired from
 * requestIdleCallback in main.tsx), so it lands AFTER first paint. track()/
 * capture() calls made before the library finishes loading are buffered and
 * flushed on load — no events are lost.
 */
import type { PostHog } from 'posthog-js';
import { parkEvents, stampLocation, takeParked, type BufferedEvent } from './analytics-handoff';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://us.i.posthog.com';

let instance: PostHog | null = null;
// Events captured before posthog-js finishes loading (or before init runs) are
// queued here and replayed once the instance exists. Each one is stamped with
// the URL and time it happened, because it may be replayed from another page:
// see analytics-handoff.ts for why a plain in-memory array was losing the
// fastest visitors entirely.
const pending: BufferedEvent[] = [];

function sessionStore(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

// If the visitor leaves before PostHog ever loaded, hand the buffer to the next
// page instead of letting it die here. `pagehide` rather than `beforeunload`:
// it also fires on mobile tab switches and does not disable the bfcache.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (instance || pending.length === 0) return;
    parkEvents(sessionStore(), pending.splice(0));
  });
}

function replay(ph: PostHog, events: BufferedEvent[]): void {
  for (const { event, properties, ts } of events) {
    ph.capture(event, properties, { timestamp: new Date(ts) });
  }
}

export async function initPostHog(): Promise<void> {
  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.warn('⚠ VITE_POSTHOG_KEY not set — analytics disabled.');
    }
    return;
  }
  if (instance) return;

  const { default: posthog } = await import('posthog-js');

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    capture_pageleave: true,
    // DEV-12078: Basic autocapture only, no heavy features
    autocapture: true,
    persistence: 'localStorage+cookie',
    // Disable session recording to save ~60 KiB
    disable_session_recording: true,
    // Disable performance/heatmaps to save ~20 KiB
    capture_dead_clicks: false,
    capture_performance: false,
    enable_heatmaps: false,

    loaded: (ph) => {
      if (import.meta.env.DEV) {
        console.log('✅ PostHog loaded, distinct_id:', ph.get_distinct_id());
      }
    },
  });

  instance = posthog;
  // First what an earlier page parked (it happened first), then anything this
  // page captured before the library was ready.
  replay(posthog, takeParked(sessionStore(), Date.now()));
  replay(posthog, pending.splice(0));
}

/** Track a custom event with properties. Buffered until posthog-js has loaded. */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (instance) {
    instance.capture(event, properties);
  } else {
    if (typeof window === 'undefined') return;
    pending.push({
      event,
      properties: stampLocation(properties, window.location),
      ts: Date.now(),
    });
  }
}

/** Capture an event by name (e.g. `$pageview`). Same buffering as track(). */
export function capture(event: string, properties?: Record<string, unknown>): void {
  track(event, properties);
}

/** Like track(), but sends immediately over sendBeacon. For events that fire
 *  right before a full-page navigation (Stripe checkout completed, then the
 *  redirect into the app), where a batched request would be cancelled. Before
 *  PostHog has loaded it falls back to the buffer, which pagehide parks. */
export function trackBeforeNavigation(event: string, properties?: Record<string, unknown>): void {
  if (instance) {
    instance.capture(event, properties, { send_instantly: true, transport: 'sendBeacon' });
    return;
  }
  track(event, properties);
}
